import { CurrencyCode } from './currencyCode';

export interface CurrencyDataType {
    currency: string;
    exchangeRate: number;
    locale: string;
    fractionDigits?: number;
}

const currencyIsYen = (currency: string): boolean => currency === 'JPY';

export const valueHasDecimals = (value: number): boolean => value - Math.floor(value) !== 0;

/* Takes a currency and returns the divisor required to show the currency with subunits, but always returns 1 for JPY */
const getCurrencySubunits = (currency: string): number => (currencyIsYen(currency) ? 1 : 100);

/* Required for Intl.NumberFormat Takes a currency and desired number of digits to show after the decimal point, but always returns 0 for JPY */
const getFractionDigits = (currency: string, fractionDigits?: number): number =>
    currencyIsYen(currency) ? 0 : typeof fractionDigits === 'number' ? fractionDigits : 2;

/* Take a USD value and returns a value in local currency */
export const getLocalAmountFromUSD = (
    usdAmount: number,
    exchangeRate: number,
    currency: string
): number => {
    if (exchangeRate <= 0) {
        throw new Error(
            'Cannot perform currency conversion. Invalid exchange rate: ' + exchangeRate
        );
    }
    const conversion = usdAmount * exchangeRate;
    return currencyIsYen(currency) ? Math.floor(conversion / 100) : conversion;
};

export const formatCurrencyAmountAsString = (amount: number, currency: string): string => {
    const value = getCurrencyAsFractionalAmount(amount, currency);

    if (valueHasDecimals(value)) {
        return value.toFixed(2);
    }

    return String(value);
};

export const getUSDFromLocalAmount = (
    localAmount: number,
    exchangeRate: number,
    currency: string
): number => {
    if (exchangeRate <= 0) {
        throw new Error(
            'Cannot perform currency conversion. Invalid exchange rate: ' + exchangeRate
        );
    }
    const conversion = Math.floor(localAmount / exchangeRate);
    return currencyIsYen(currency) ? conversion * 100 : conversion;
};

/* Take a local currency subunit amount and return the main (with subunit fraction) amount */
export const getCurrencyAsFractionalAmount = (localAmountInSubunits: number, currency: string) => {
    const subunits = getCurrencySubunits(currency);
    return subunits ? localAmountInSubunits / subunits : localAmountInSubunits;
};

/* Take a local currency subunit amount and return the main (with subunit fraction) amount */
export const getCurrencyWholeAmount = (localAmountWithSubunits: number, currency: string) => {
    const subunits = getCurrencySubunits(currency);
    return subunits ? Math.round(localAmountWithSubunits * subunits) : localAmountWithSubunits;
};

/*  Given a USD amount in subunits
    convert to local currency, get the fractional value
    and return formatted value as string
 */
export const formatUsdAmountAsLocalCurrencyString = (
    usdAmountInSubunits: number,
    { currency, exchangeRate, locale, fractionDigits }: CurrencyDataType
) => {
    const fraction: number = getFractionDigits(currency, fractionDigits);
    const localAmountInSubunits = getLocalAmountFromUSD(
        usdAmountInSubunits,
        exchangeRate,
        currency
    );

    const localAmountFractional = getCurrencyAsFractionalAmount(localAmountInSubunits, currency);
    const formatter: Intl.NumberFormat = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: fraction,
        minimumFractionDigits: fraction,
    });

    return formatter.format(localAmountFractional);
};

// Note: When using this, it is recommended to check if Vista is supporting any other locales,
//       otherwise, it will fall back to USD which can be undesirable and confusing.

const REGION_TO_CURRENCY: Record<string, CurrencyCode> = {
    // Commonly translated

    DK: CurrencyCode.DKK,
    DE: CurrencyCode.EUR,
    CA: CurrencyCode.CAD,
    GB: CurrencyCode.GBP,
    IE: CurrencyCode.EUR,
    IN: CurrencyCode.INR,
    US: CurrencyCode.USD,
    ES: CurrencyCode.EUR,
    FI: CurrencyCode.EUR,
    FR: CurrencyCode.EUR,
    IT: CurrencyCode.EUR,
    NO: CurrencyCode.NOK,
    NL: CurrencyCode.EUR,
    PT: CurrencyCode.EUR,
    SE: CurrencyCode.SEK,
    AU: CurrencyCode.AUD,
    NZ: CurrencyCode.NZD,
    SG: CurrencyCode.SGD,

    // Not commonly translated but still supported

    BE: CurrencyCode.EUR,
    AT: CurrencyCode.EUR,
    CH: CurrencyCode.CHF,
    BR: CurrencyCode.BRL,

    // Not currently supported

    // 'JP': JPY,
    // 'KR': KRW,
    // 'CN': CNY,
};

export function getCurrencyFromLocale(locale: string): string {
    const region = locale.split('-')[1] || locale.split('_')[1];

    if (!region) {
        return CurrencyCode.USD;
    }

    return REGION_TO_CURRENCY[region.toUpperCase()] || CurrencyCode.USD;
}
